import DOMPurify from 'isomorphic-dompurify';
import {
  ReslinkEventForEmail,
  HotelDetailsForEmail,
  RateDetail,
  ReslinkEventForEmailDtt,
  HotelGroupEventDetailsForEmail,
  SourceCRS,
} from '@marriott/mi-groups-graphql';
import { formatAmount, formatUTCDate, getFormattedDate, getFormattedDisplayDate } from '../../utils';
import {
  getBaseRate,
  getFilteredRatesDtt,
  getRateRange as getMinMaxRates,
  getRateRangeDtt as getMinMaxRatesDtt,
  getMarshaEnabledInventoryTypeCodes,
  getBranchIoUrl,
} from './reslink';
import { EventSummaryDetails } from '../organisms/rooming-list/PlannerNewEvent/EventDetailsBanner/EventDetailsBanner.types';
import { DEFAULT_LANG, CHINESE_LANG, CHINESE_DOMAIN_SUFFIX, CF_LOCALE_MAP } from '../../constants';
import { ALLOWED_ATTRIBUTES_FOR_SANITIZING_MESSAGE } from '../constants';
import { BranchIO } from '../organisms/reslink/EventList/EventList.types';

export const formatEmailMessage = (message = '', eventDetails?: EventSummaryDetails, rates?: RateDetail[]) => {
  const { title = '', cutoffDate = '', startDate = '', endDate = '', hotelName = '' } = eventDetails || {};

  const currency = rates?.[0]?.rateAmounts?.length ? getBaseRate(rates?.[0]?.rateAmounts).currency : '';
  const rateList = rates?.map(rate => getBaseRate(rate.rateAmounts)?.rateValue) || [];
  const [minRate, maxRate] = getRateRange(rateList);

  const updatedMessage =
    minRate === maxRate
      ? message.replace('<span> {minRate} </span><span>{currency} - {maxRate} </span>', '<span> {maxRate} </span>')
      : message;

  const formattedStartDate = startDate ? getFormattedDate(formatUTCDate(startDate), 'dateWithDayMonthAndYear') : '';

  const formattedEndDate = endDate ? getFormattedDate(formatUTCDate(endDate), 'dateWithDayMonthAndYear') : '';

  const formattedCutOffDate = cutoffDate ? getFormattedDate(formatUTCDate(cutoffDate), 'dateWithDayMonthAndYear') : '';

  return updatedMessage
    .replace(/{eventName}/g, title)
    .replace(/{arrivalDate}/g, formattedStartDate)
    .replace(/{departureDate}/g, formattedEndDate)
    .replace(/{cutOffDate}/g, formattedCutOffDate)
    .replace(/{hotelName}/g, hotelName)
    .replace(/{minRate}/g, minRate === '-' ? '-' : formatAmount(+minRate).toString())
    .replace(/{maxRate}/g, maxRate === '-' ? '-' : formatAmount(+maxRate).toString())
    .replace(/{currency}/g, currency);
};

export const formatReslinkEmailMessage = (
  isGroup: boolean,
  message = '',
  reslinkEvent: ReslinkEventForEmail,
  labels: { forText: string; perNight: string; lastDayToBook: string },
  emailLocale: string,
  branchIo: BranchIO
) => {
  const { id, eventName, hotelDetails, startDate, endDate } = reslinkEvent || {};
  const [language, region] = emailLocale.split('-');
  const dateLocale = `${language}-${region.toUpperCase()}`;

  const hotelDetailSection = hotelDetails?.map((hotel: HotelDetailsForEmail) => {
    const { property, groupCodes, miniHotelInfo } = hotel;
    const propertyName = property?.basicInformation?.name;
    const { basicInformation, rates } = miniHotelInfo?.[0]?.node || {};
    const cutoffDate = basicInformation?.cutoffDate;

    const filteredRates = rates?.filter(({ groupCode }) => groupCodes.includes(groupCode as string)) || [];
    const { minRate, maxRate, currency } = getMinMaxRates(filteredRates);

    return `<li class="l-padding-bottom-none t-font-m l-padding-top-none js-form-text">
    ${
      minRate !== maxRate
        ? `<strong>
            <span> ${propertyName} </span>
            ${
              isGroup
                ? `<span>${labels.forText}</span>
                  <span>${formatAmount(+minRate)}  ${currency}</span>
                  <span> - </span>
                  <span class="d-inline-block">${formatAmount(+maxRate)} ${currency}</span>
                  <span> ${labels.perNight}</span>`
                : ''
            }
          </strong>`
        : `<strong>
            <span> ${propertyName} </span>
            ${
              isGroup
                ? `<span>${labels.forText}</span>
                  <span> 
                    ${formatAmount(+maxRate)} 
                  </span>
                  <span>${currency}</span>
                  <span> ${labels.perNight}</span>`
                : ''
            }
          </strong>`
    } ${
      isGroup && cutoffDate
        ? `- <strong><span class="js-form-text">${labels.lastDayToBook}</span> ${getFormattedDisplayDate(
            formatUTCDate(cutoffDate),
            'longDateWithDayMonthAndYear',
            dateLocale
          )}</strong>`
        : ''
    }
        </li>`;
  });

  const formattedStartDate = startDate
    ? getFormattedDisplayDate(formatUTCDate(startDate), 'longDateWithDayMonthAndYear', dateLocale)
    : '';

  const formattedEndDate = endDate
    ? getFormattedDisplayDate(formatUTCDate(endDate), 'longDateWithDayMonthAndYear', dateLocale)
    : '';

  const eventIdTokens = id?.split('_');

  //get branchIO url
  const domainValue = `${window.location.origin.replace(CHINESE_DOMAIN_SUFFIX, '')}${
    emailLocale === DEFAULT_LANG.toLowerCase()
      ? ''
      : emailLocale === CHINESE_LANG.toLowerCase()
      ? CHINESE_DOMAIN_SUFFIX
      : `/${CF_LOCALE_MAP[emailLocale]?.locale || ''}`
  }`;

  let url = `${domainValue}/event-reservations/reservation-link.mi?id=${eventIdTokens[0]}&key=${eventIdTokens[1]}`;
  if (isGroup && branchIo?.url[window.location.host as string]) {
    url = getBranchIoUrl(branchIo.url[window.location.host as string], eventIdTokens[0], eventIdTokens[1]);
  }

  try {
    return message
      .replace(/{eventName}/g, eventName)
      .replace(/{arrivalDate}/g, formattedStartDate)
      .replace(/{departureDate}/g, formattedEndDate)
      .replace(/{id}/g, eventIdTokens[0])
      .replace(/{key}/g, eventIdTokens[1])
      .replace(/{availableHotels}/g, hotelDetailSection.join(''))
      .replace(/{domain}/g, `${domainValue}`)
      .replace(/{reslinkGuestUrl}/g, url);
  } catch (error) {
    return '';
  }
};

export const formatReslinkEmailMessageDtt = (
  isGroup: boolean,
  message = '',
  reslinkEvent: ReslinkEventForEmailDtt,
  labels: { forText: string; perNight: string; lastDayToBook: string },
  emailLocale: string,
  branchIo: BranchIO,
  isBranchIoAppDownloadEnabled = false
) => {
  const { id, eventName, startDate, endDate, hotelGroupEventDetails } = reslinkEvent || {};
  const [language, region] = emailLocale.split('-');
  const dateLocale = `${language}-${region.toUpperCase()}`;

  const hotelDetailSection = hotelGroupEventDetails?.map((hotel: HotelGroupEventDetailsForEmail) => {
    const { property, inventoryTypeCodes, groupEventDetails, groupBlockCode } = hotel;
    const propertyName = property?.basicInformation?.name;
    const { releaseInventory, pricePlans } = groupEventDetails?.groupBlock?.groupContract || {};
    const { amountRules } = pricePlans?.[0].priceGridDetails || {};
    const cutoffDate = releaseInventory?.releaseInfo?.date;

    const isMarshaProperty = groupEventDetails?.groupBlock?.sourceCRS === SourceCRS.MARSHA;
    const enabledInventoryTypeCodes = isMarshaProperty
      ? getMarshaEnabledInventoryTypeCodes(inventoryTypeCodes, amountRules, groupBlockCode)
      : inventoryTypeCodes;
    const filteredRates = getFilteredRatesDtt(enabledInventoryTypeCodes, amountRules, isMarshaProperty);
    const { minRate, maxRate, currency } = getMinMaxRatesDtt(filteredRates);

    return `<li class="l-padding-bottom-none t-font-m l-padding-top-none js-form-text">
    ${
      minRate !== maxRate
        ? `<strong>
            <span> ${propertyName} </span>
            ${
              isGroup
                ? `<span>${labels.forText}</span>
                  <span>${formatAmount(+minRate)}  ${currency}</span>
                  <span> - </span>
                  <span class="d-inline-block">${formatAmount(+maxRate)} ${currency}</span>
                  <span> ${labels.perNight}</span>`
                : ''
            }
          </strong>`
        : `<strong>
            <span> ${propertyName} </span>
            ${
              isGroup
                ? `<span>${labels.forText}</span>
                  <span> 
                    ${formatAmount(+maxRate)} 
                  </span>
                  <span>${currency}</span>
                  <span> ${labels.perNight}</span>`
                : ''
            }
          </strong>`
    } ${
      isGroup && cutoffDate
        ? `- <strong><span class="js-form-text">${labels.lastDayToBook}</span> ${getFormattedDisplayDate(
            formatUTCDate(cutoffDate),
            'longDateWithDayMonthAndYear',
            dateLocale
          )}</strong>`
        : ''
    }
        </li>`;
  });

  const formattedStartDate = startDate
    ? getFormattedDisplayDate(formatUTCDate(startDate), 'longDateWithDayMonthAndYear', dateLocale)
    : '';

  const formattedEndDate = endDate
    ? getFormattedDisplayDate(formatUTCDate(endDate), 'longDateWithDayMonthAndYear', dateLocale)
    : '';

  const eventIdTokens = id?.split('_');

  //get branchIO url
  const domainValue = `${window.location.origin.replace(CHINESE_DOMAIN_SUFFIX, '')}${
    emailLocale === DEFAULT_LANG.toLowerCase()
      ? ''
      : emailLocale === CHINESE_LANG.toLowerCase()
      ? CHINESE_DOMAIN_SUFFIX
      : `/${CF_LOCALE_MAP[emailLocale]?.locale || ''}`
  }`;

  let url = `${domainValue}/event-reservations/reservation-link.mi?id=${eventIdTokens[0]}&key=${eventIdTokens[1]}`;
  if (isGroup && branchIo.url[window.location.host as string]) {
    const branchIoUrl =
      isBranchIoAppDownloadEnabled &&
      emailLocale?.toLowerCase() === DEFAULT_LANG.toLowerCase() &&
      branchIo.deepViewURL?.[window.location.host as string]
        ? branchIo.deepViewURL[window.location.host as string]
        : branchIo.url[window.location.host as string];
    url = getBranchIoUrl(branchIoUrl, eventIdTokens[0], eventIdTokens[1]);
  }

  try {
    return message
      .replace(/{eventName}/g, eventName)
      .replace(/{arrivalDate}/g, formattedStartDate)
      .replace(/{departureDate}/g, formattedEndDate)
      .replace(/{id}/g, eventIdTokens[0])
      .replace(/{key}/g, eventIdTokens[1])
      .replace(/{availableHotels}/g, hotelDetailSection.join(''))
      .replace(/{domain}/g, `${domainValue}`)
      .replace(/{reslinkGuestUrl}/g, url);
  } catch (error) {
    return '';
  }
};

const getRateRange = (rateList: (string | number)[]) => {
  const filteredRateList = rateList?.filter(val => typeof val === 'number' && !isNaN(val));
  if (!filteredRateList?.length) {
    return ['-', '-'];
  }
  const minRate = Math.min(...(filteredRateList as number[]));
  const maxRate = Math.max(...(filteredRateList as number[]));
  return [minRate, maxRate];
};

export const sanitizeMessage = (message: string) => {
  const sanitizedDocument = DOMPurify.sanitize(message, {
    ALLOWED_ATTR: ALLOWED_ATTRIBUTES_FOR_SANITIZING_MESSAGE,
    RETURN_DOM: true,
    WHOLE_DOCUMENT: true,
  }) as Document;

  sanitizedDocument.querySelectorAll('.sr-only').forEach(element => element.remove());
  return new XMLSerializer().serializeToString(sanitizedDocument);
};
